import DataTable, { type ColumnDef } from "@/components/core/data-table";
import { documentationChecklistsQueryOptions } from "@/features/dashboard/api/queries/documentation-checklist-query";
import type { DocumentationChecklist } from "@/features/dashboard/types/documentation-checklist";
import { IconCalendar, IconList, IconMapPin } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";

interface DocumentationChecklistListProps {
	documentationId: number;
	categoryName?: string;
}

export function DocumentationChecklistList({ documentationId, categoryName }: DocumentationChecklistListProps) {
	const { data, isLoading, error } = useQuery({
		...documentationChecklistsQueryOptions({
			documentation_id: documentationId,
			per_page: 50,
			include: "checklist",
		}),
	});

	const columns: ColumnDef<DocumentationChecklist>[] = [
		{
			id: "checklist_name",
			label: "Checklist Name",
			cell: ({ data }) => (
				<div className="flex items-center gap-2">
					<IconList className="h-4 w-4 text-gray-400" />
					<div>
						<div className="font-medium text-sm">{data.checklist_name}</div>
						{data.checklist?.name && <div className="text-xs text-gray-500">{data.checklist.name}</div>}
					</div>
				</div>
			),
		},
	];

	if (isLoading) {
		return (
			<div className="space-y-4">
				<div className="flex items-center justify-between">
					<div>
						<h3 className="text-lg font-semibold">Documentation Checklists</h3>
						{categoryName && <p className="text-sm text-gray-600">Checklists for {categoryName}</p>}
					</div>
				</div>
				<div className="border rounded-lg p-8">
					<div className="flex items-center justify-center">
						<div className="text-center">
							<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" />
							<p className="mt-2 text-sm text-gray-600">Loading checklists...</p>
						</div>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="space-y-4">
			<div className="flex items-center justify-between">
				<div>
					<h3 className="text-lg font-semibold">Documentation Checklists</h3>
					{categoryName && <p className="text-sm text-gray-600">Checklists for {categoryName}</p>}
				</div>
				<div className="text-sm text-gray-500">
					{data?.data?.length || 0} checklist{(data?.data?.length || 0) !== 1 ? "s" : ""}
				</div>
			</div>

			<div className="border rounded-lg">
				<DataTable
					columns={columns}
					data={data?.data || []}
					isLoading={isLoading}
					emptyState={{
						title: "No checklists found",
						description: categoryName
							? `No checklists have been added for this ${categoryName} documentation yet.`
							: "No checklists have been added for this documentation yet.",
					}}
				/>
			</div>
		</div>
	);
}
